import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { formatINR } from "@/project/data/sample";
import { domainName } from "@/project/data/network";
import { useOrganisations, useSetOrgStatus } from "@/project/lib/queries";
import { downloadCsv } from "@/project/lib/receipt";

export const Route = createFileRoute("/admin/partners")({ component: AdminPartners });

function AdminPartners() {
  const { data: partners = [], isLoading } = useOrganisations({ all: true });
  const setStatus = useSetOrgStatus();
  const [q, setQ] = useState("");

  const rows = partners.filter((p) =>
    [p.name, p.city, p.domain].join(" ").toLowerCase().includes(q.toLowerCase()),
  );
  const live = partners.filter((p) => p.status === "approved").length;
  const review = partners.filter((p) => p.status !== "approved").length;
  const raised = partners.reduce((s, p) => s + (p.total_raised ?? 0), 0);

  async function change(id: string, status: "approved" | "suspended") {
    try {
      await setStatus.mutateAsync({ id, status });
      toast.success(status === "approved" ? "Partner is live." : "Partner suspended.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed — super admin access required.");
    }
  }

  return (
    <>
      <PageHead
        title="Partner organisations"
        subtitle="Every panjrapol, gaushala, NGO and associated foundation on the network."
        action={
          <button
            onClick={() =>
              downloadCsv("partner-organisations", [
                ["Name", "Type", "Domain", "City", "State", "Status", "80G", "Raised", "Donors"],
                ...partners.map((p) => [
                  p.name,
                  p.org_type ?? "",
                  p.domain ?? "",
                  p.city ?? "",
                  p.state ?? "",
                  p.status,
                  p.eligible_80g ? "Yes" : "No",
                  p.total_raised ?? 0,
                  p.donor_count ?? 0,
                ]),
              ])
            }
            className="rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold"
          >
            Export CSV
          </button>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total partners" value={String(partners.length)} tone="primary" />
        <StatCard label="Live" value={String(live)} tone="good" />
        <StatCard label="Needs attention" value={String(review)} tone="warn" />
        <StatCard label="Lifetime raised" value={formatINR(raised)} />
      </div>
      <div className="mt-6">
        <Panel
          title="Directory"
          action={
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search partners…"
              className="w-44 rounded-full border border-border bg-background px-3 py-1.5 text-xs"
            />
          }
        >
          {isLoading && <p className="py-6 text-sm text-foreground/70">Loading partners…</p>}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-foreground/70">
                <tr className="border-b border-border text-left">
                  <th className="py-2">Organisation</th><th>Domain</th><th>City</th><th>Raised</th><th>80G</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="py-3 font-medium">{p.name}<span className="block text-xs text-foreground/70">{p.org_type}</span></td>
                    <td>{p.domain ? domainName(p.domain) : "—"}</td>
                    <td>{p.city}</td>
                    <td className="font-semibold text-[var(--maroon)]">{formatINR(p.total_raised ?? 0)}</td>
                    <td>{p.eligible_80g ? "Yes" : "No"}</td>
                    <td>
                      {p.status === "approved" ? (
                        <span className="inline-flex items-center gap-1 text-[var(--success)]"><CheckCircle2 className="h-3 w-3" /> Live</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 capitalize text-[var(--saffron-deep)]"><ShieldAlert className="h-3 w-3" /> {p.status}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <Link to="/organisations/$id" params={{ id: p.slug }} className="rounded-full border border-border bg-background px-3 py-1 text-xs">View</Link>
                      {p.status === "approved" ? (
                        <button onClick={() => change(p.id, "suspended")} className="ml-2 rounded-full bg-[var(--destructive)] px-3 py-1 text-xs font-semibold text-white">Suspend</button>
                      ) : (
                        <button onClick={() => change(p.id, "approved")} className="ml-2 rounded-full bg-[var(--success)] px-3 py-1 text-xs font-semibold text-white">Approve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}
